import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Send, Heart, AlertTriangle, Phone, Trash2, Eraser } from "lucide-react";
import { api } from "../../lib/api";
import Markdown from "../Markdown";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

/**
 * "שפוך את הלב" — Pour-Out-Your-Heart panel.
 *
 * Lives inside the Notebook page (the "heart" tab). Renders as a single
 * continuous notebook page with ruled lines — entries flow downward in
 * pairs (user prose → Torah-grounded comfort, or user prose → crisis
 * referral box if our safety screen fired).
 *
 * Differences from the Chavruta panel:
 *   • Style: ruled notebook paper. No chat bubbles. Letters live on the lines.
 *   • Tone is intentionally short and warm — the backend caps the reply.
 *   • Crisis path uses a distinct amber-bordered "caring" box that
 *     surfaces real hotlines (ER"AN 1201 + SAHAR online chat) so the
 *     person can act, not read more text.
 *   • Persistent footer disclaimer is shown ALL the time (not just
 *     during a turn), per the user spec.
 */
export default function HeartPanel() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null);
  // `null` = no dialog open; string id ⇒ "delete this entry" dialog;
  // "__ALL__" ⇒ "wipe everything" dialog.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pageRef = useRef(null);
  const composerRef = useRef(null);
  // Track the previous entry count so the auto-scroll-to-bottom only
  // fires when a NEW entry is appended — not on every re-render. This
  // is the root cause of the "jumpy" scroll users reported: the old
  // effect re-ran on every `entries`/`sending` change (including
  // optimistic updates and quota refreshes), constantly snapping the
  // user back to the bottom while they tried to read earlier entries.
  const prevCountRef = useRef(0);

  // Initial load: pull the user's past entries + remaining quota.
  useEffect(() => {
    (async () => {
      try {
        const [{ items }, q] = await Promise.all([
          api.heartEntries(50),
          api.heartQuota(),
        ]);
        // We want newest at the bottom (notebook reading order). The
        // server returns newest-first; flip for display.
        setEntries((items || []).slice().reverse());
        setQuota(q);
      } catch (e) {
        // Non-fatal — empty notebook is still a valid state.
        console.warn("heart load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Scroll-to-bottom — only when an entry is APPENDED and only if the
  // user is currently near the bottom (so reading older entries
  // doesn't get yanked back). We intentionally don't depend on
  // `sending` here; that re-render was the source of the jumpy
  // scrolling.
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = entries.length;
    if (entries.length <= prev) return;            // not a new entry
    // Mobile (≤768): there is no inner scroll — the page scrolls as a
    // single unit. Use `scrollIntoView` on the composer so we land on
    // the freshest line WITHOUT teleporting in the middle of an
    // unrelated state change.
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (isMobile) {
      composerRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
      return;
    }
    const el = pageRef.current;
    if (!el) return;
    const NEAR_BOTTOM = 120; // px tolerance
    const wasNearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) <= NEAR_BOTTOM;
    if (wasNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [entries.length]);

  const handleSubmit = useCallback(async () => {
    const q = text.trim();
    if (!q || sending) return;
    if (quota && quota.remaining <= 0) {
      toast.error(t("heart_panel.toast_quota", { limit: quota.limit }));
      return;
    }
    setSending(true);
    // Optimistic insert of the user's own line, so the page reacts
    // instantly even before the Sefaria+LLM round-trip finishes.
    const placeholder = {
      id: `local-${Date.now()}`,
      question: q,
      answer: "",
      is_crisis: false,
      pending: true,
      created_at: new Date().toISOString(),
    };
    setEntries((prev) => [...prev, placeholder]);
    setText("");
    try {
      const res = await api.heartPour(q, i18n.language === "en" ? "en" : "he");
      if (res?.error === "daily_limit_reached") {
        setEntries((prev) => prev.filter((e) => e.id !== placeholder.id));
        const msg = (i18n.language === "en" && res.message_en) || res.message_he;
        toast.error(msg || t("heart_panel.toast_quota_default"));
        if (res.limit) setQuota((qu) => ({ ...(qu || {}), remaining: 0, limit: res.limit }));
        return;
      }
      setEntries((prev) =>
        prev.map((e) => (e.id === placeholder.id ? res.entry : e)),
      );
      if (res.quota) setQuota(res.quota);
    } catch (e) {
      setEntries((prev) => prev.filter((x) => x.id !== placeholder.id));
      setText(q);
      const detail = e?.response?.data?.detail || t("heart_panel.toast_send_failed");
      toast.error(detail);
    } finally {
      setSending(false);
      // Return focus to composer for the next thought.
      composerRef.current?.focus?.();
    }
  }, [text, sending, quota, t]);

  /**
   * Delete handlers — both routes do HARD deletes server-side. We
   * optimistically update local state on success so the page reacts
   * instantly without a refetch. On failure we restore the previous
   * state and surface a toast.
   */
  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const wasAll = pendingDelete === "__ALL__";
    const targetId = wasAll ? null : pendingDelete;
    const prevEntries = entries;
    try {
      if (wasAll) {
        const res = await api.heartDeleteAll();
        setEntries([]);
        // Quota was reset server-side too — refresh so the UI reflects it.
        try {
          const q = await api.heartQuota();
          setQuota(q);
        } catch { /* non-fatal */ }
        toast.success(
          res?.deleted > 0
            ? t("heart_panel.toast_deleted_n", { count: res.deleted })
            : t("heart_panel.toast_deleted_all"),
        );
      } else {
        await api.heartDeleteEntry(targetId);
        setEntries((prev) => prev.filter((e) => e.id !== targetId));
        toast.success(t("heart_panel.toast_deleted_one"));
      }
    } catch (e) {
      console.warn("heart delete failed", e);
      // Rollback in case of optimistic UI drift; for the all-clear path
      // we only mutate state inside `try`, so nothing to undo there.
      setEntries(prevEntries);
      const status = e?.response?.status;
      if (status === 404) {
        toast.error(t("heart_panel.toast_not_found"));
      } else {
        toast.error(t("heart_panel.toast_delete_failed"));
      }
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, entries, t]);

  return (
    <div
      dir={isEn ? "ltr" : "rtl"}
      className="heart-panel-shell"
      data-testid="notebook-tab-panel-heart"
    >
      <div className="heart-header">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-rose-700" strokeWidth={1.6} />
          <h2 className="font-heading text-base sm:text-lg text-stone-800">
            {t("heart_panel.title")}
          </h2>
        </div>
        <p className="text-xs text-stone-600 mt-1 leading-snug">
          {t("heart_panel.subtitle")}
        </p>
        {quota && (
          <span
            className="mt-2 inline-block text-[10px] font-mono text-stone-500"
            data-testid="heart-quota"
            dir="ltr"
          >
            {quota.remaining}/{quota.limit}
          </span>
        )}
      </div>

      {/* The notebook "page" — ruled paper, entries flow downward. */}
      <div
        ref={pageRef}
        className="heart-page"
        data-testid="heart-page"
      >
        {loading && (
          <div className="text-center text-stone-500 text-sm py-12">
            <Loader2 className="h-5 w-5 animate-spin inline-block" strokeWidth={1.5} />
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center text-stone-500 py-12 px-4" data-testid="heart-empty-state">
            <Heart className="h-8 w-8 mx-auto text-stone-300 mb-3" strokeWidth={1.4} />
            <p className="text-sm leading-relaxed max-w-sm mx-auto">
              {t("heart_panel.empty_prompt")}
            </p>
          </div>
        )}

        {entries.map((entry) => (
          <Entry
            key={entry.id}
            entry={entry}
            onDelete={
              entry.pending ? undefined : () => setPendingDelete(entry.id)
            }
          />
        ))}

        {sending && entries[entries.length - 1]?.pending && (
          <div className="heart-pending" data-testid="heart-pending">
            <Loader2 className="h-3.5 w-3.5 animate-spin inline-block ml-1" strokeWidth={1.5} />
            {t("heart_panel.thinking")}
          </div>
        )}

        {/* Right-to-be-forgotten — clear-all button. Subtle by design:
            it lives at the very bottom of the page so it's easy to find
            without being scary or one-click destructive (the AlertDialog
            below handles the confirmation). */}
        {!loading && entries.length > 0 && (
          <div className="heart-clear-all-row">
            <button
              type="button"
              onClick={() => setPendingDelete("__ALL__")}
              data-testid="heart-clear-all-btn"
              className="heart-clear-all-btn"
            >
              <Eraser className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("heart_panel.clear_all_btn")}
            </button>
          </div>
        )}
      </div>

      {/* Composer — handwriting-styled textarea. */}
      <div className="heart-composer">
        <textarea
          ref={composerRef}
          data-testid="heart-composer-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          rows={3}
          maxLength={4000}
          placeholder={t("heart_panel.placeholder")}
          className="heart-textarea"
          dir={isEn ? "ltr" : "rtl"}
        />
        <div className="flex items-center justify-between mt-2 gap-2">
          <p className="text-[10px] text-stone-500/80 leading-snug flex-1">
            {t("heart_panel.footer_disclaimer")}
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!text.trim() || sending}
            data-testid="heart-send"
            aria-label={t("heart_panel.send_aria")}
            className="heart-send-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <Send className="h-4 w-4 rotate-180" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Destructive-action dialog. One component, two modes (single
          vs all) — keeps focus management & a11y in one place. */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v && !deleting) setPendingDelete(null); }}
      >
        <AlertDialogContent dir={isEn ? "ltr" : "rtl"} data-testid="heart-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">
              {pendingDelete === "__ALL__"
                ? t("heart_panel.delete_all_title")
                : t("heart_panel.delete_one_title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              {pendingDelete === "__ALL__"
                ? t("heart_panel.delete_all_desc")
                : t("heart_panel.delete_one_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="heart-delete-cancel"
              disabled={deleting}
            >
              {t("heart_panel.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // AlertDialogAction auto-closes by default — block that
                // so we can keep the dialog open while the request is
                // in-flight and close it ourselves on success/error.
                e.preventDefault();
                confirmDelete();
              }}
              data-testid="heart-delete-confirm"
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                pendingDelete === "__ALL__"
                  ? t("heart_panel.delete_all_confirm")
                  : t("heart_panel.delete_one_confirm")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// =====================================================================
// Single entry — user prose + (Torah comfort | crisis referral)
// =====================================================================

function Entry({ entry, onDelete }) {
  const { t } = useTranslation();
  const isCrisis = !!entry.is_crisis;
  return (
    <article className="heart-entry" data-testid={`heart-entry-${entry.id}`}>
      <div className="heart-entry-head">
        <div className="heart-user-prose flex-1" data-testid="heart-user-prose">
          {entry.question}
        </div>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            data-testid={`heart-entry-delete-${entry.id}`}
            aria-label={t("heart_panel.delete_entry_aria")}
            title={t("heart_panel.delete_entry_aria")}
            className="heart-entry-delete-btn"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        )}
      </div>

      {entry.pending && !entry.answer ? null : isCrisis ? (
        <CrisisBox text={entry.answer} />
      ) : (
        <div className="heart-torah-reply" data-testid="heart-torah-reply" dir="rtl">
          <Markdown>{entry.answer}</Markdown>
        </div>
      )}
    </article>
  );
}


/**
 * Visually distinct "caring" box for crisis responses. Amber border
 * + clickable phone link so the user can call immediately on mobile,
 * not just read. Lives outside the chat flow on purpose.
 *
 * Hotline numbers are intentionally Israeli (ER"AN 1201, SAHAR) — the
 * Phase-2 brief will add international + US-Jewish hotlines (988, Chai
 * Lifeline, Amudim). Until then we surface the existing Israeli lines
 * regardless of UI language, since they are the lines our crisis prompt
 * is currently trained against.
 */
function CrisisBox({ text }) {
  const { t } = useTranslation();
  return (
    <div
      className="heart-crisis-box"
      data-testid="heart-crisis-box"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle
          className="h-4 w-4 text-amber-700 mt-0.5 shrink-0"
          strokeWidth={1.6}
        />
        <h3 className="font-heading text-sm text-amber-900">
          {t("heart_panel.distress_title")}
        </h3>
      </div>
      <p className="text-sm leading-relaxed text-stone-800 whitespace-pre-wrap">
        {text}
      </p>
      <div className="mt-3 flex flex-col gap-1.5">
        <a
          href="tel:1201"
          data-testid="heart-crisis-call-eran"
          className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm rounded-full px-4 py-2 self-start transition-colors"
        >
          <Phone className="h-4 w-4" strokeWidth={1.8} />
          {t("heart_panel.distress_eran")}
        </a>
        <a
          href="https://sahar.org.il/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="heart-crisis-chat-sahar"
          className="text-xs text-amber-800 hover:text-amber-900 underline self-start"
        >
          {t("heart_panel.distress_sahar")}
        </a>
      </div>
    </div>
  );
}
